import { z } from "zod";
import { e164Schema } from "./phone.js";
import { CALLER_VERIFICATION_STATES, callerVerificationSchema } from "./telephony.js";
import { idSchema, singleLineTextSchema } from "./text.js";
// Il parco dei cellulari dell'ufficio (piano M4, Task 16, D11 e D17; questione 167; TEL-3): i cellulari italiani con cui le
// persone dell'azienda chiamano, uno per persona, e lo stesso anche per più persone. Le rotte sono `/api/caller-numbers`:
// - `GET` l'elenco, per chi gestisce il team o ha la sezione «Numeri per chiamare» (`settings.phone_numbers`), con quello che
//   chi guarda può fare: assegnare (`canAssign`, la sezione) e aggiungere (`canAdd`, la sezione fuori da «Guarda come…»);
// - `POST` aggiunge un cellulare e ne chiede la verifica al fornitore: la risposta porta il codice di sei cifre da digitare sul
//   telefono, che non si salva da nessuna parte; `POST …/:id/verify` la chiede di nuovo, `POST …/import` prende i cellulari già
//   verificati presso il fornitore, `DELETE …/:id` toglie un cellulare dal parco. Tutte e quattro per chi ha la sezione (il Super
//   admin e l'Admin dell'azienda di partenza, TEL-3 del 21/10/2026) e per il contesto di piattaforma, mai in «Guarda come…»;
//   le telefonate di verifica hanno un limite l'ora (`429 rate_limited`);
// - `PUT …/:id/assignee` lo dà a una persona attiva, o glielo toglie, con la versione letta.
// Il cellulare diventa verificato soltanto con la conferma firmata del fornitore (il webhook `verification`).
/**
 * Gli stati di un cellulare del parco: i tre della verifica presso il fornitore (`CALLER_VERIFICATION_STATES`, definiti una volta
 * in `telephony.ts`) e `removed`, tolto dal parco. Il database li ripete come elenco (`CALLER_NUMBER_STATES` dello schema
 * operativo) e un test del server li confronta.
 */
export const CALLER_NUMBER_STATES = [...CALLER_VERIFICATION_STATES, "removed"];
export const callerNumberStateSchema = z.enum(CALLER_NUMBER_STATES);
/** Una persona a cui è dato un cellulare, con il nome che ha adesso. */
export const callerNumberAssigneeSchema = z.object({ membershipId: z.string(), displayName: z.string() });
/**
 * Un cellulare del parco: il numero intero (lo vede soltanto chi gestisce il parco), il nome che gli dà il gestore (vuoto per
 * uno importato dal fornitore), lo stato, le persone a cui è dato (nessuna se è libero; più d'una se lo condividono, TEL-3), in
 * ordine di assegnazione, la versione per l'assegnazione e quando è stato verificato.
 */
export const callerNumberRowSchema = z.object({
    id: z.string(),
    e164: z.string(),
    label: z.string().nullable(),
    state: callerNumberStateSchema,
    assignees: z.array(callerNumberAssigneeSchema),
    version: z.number().int(),
    verifiedAt: z.string().nullable(),
});
/** `GET /api/caller-numbers` e `POST /api/caller-numbers/import`: i cellulari non tolti e che cosa può fare chi guarda. */
export const callerNumbersResponseSchema = z.object({ numbers: z.array(callerNumberRowSchema), canAssign: z.boolean(), canAdd: z.boolean() });
/** `POST /api/caller-numbers`: il cellulare italiano e il suo nome, da 1 a 60 caratteri. */
export const addCallerNumberBodySchema = z.strictObject({ e164: e164Schema, label: singleLineTextSchema(60) });
/** `POST /api/caller-numbers` e `POST …/:id/verify`: il cellulare in verifica e come chi lo tiene completa la verifica. */
export const addCallerNumberResponseSchema = z.object({ number: callerNumberRowSchema, verification: callerVerificationSchema });
/**
 * `PUT /api/caller-numbers/:id/assignee`: la persona, se il cellulare diventa suo (`assigned: true`) o non lo è più (`false`), e la
 * versione letta del cellulare. Le altre persone del cellulare non cambiano.
 */
export const assignCallerNumberBodySchema = z.strictObject({ membershipId: idSchema, assigned: z.boolean(), expectedVersion: z.number().int().min(1) });
/** `PUT …/:id/assignee` e `DELETE …/:id`: il cellulare com'è dopo il cambiamento. */
export const callerNumberResponseSchema = z.object({ number: callerNumberRowSchema });
//# sourceMappingURL=caller-numbers.js.map